import React, { useState, useEffect, useCallback } from 'react';
import {
  Lightbulb,
  Star,
  Filter,
  Search,
  Zap,
  Brain,
  User,
  Shuffle,
  BookOpen,
  Plus,
  ExternalLink,
  Heart,
  MessageSquare,
  Trash2,
  Sparkles,
  Loader2,
  CheckCircle2,
  FileText
} from 'lucide-react';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { supabase } from '../../../lib/supabase';
import EssayTopicDevelopment from './EssayTopicDevelopment';

/**
 * Essay Ideas Library
 *
 * Central repository for all saved essay ideas from:
 * - Hidden Competencies interviews
 * - Character & Identity interviews
 * - Strategic Assessment
 * - Quick Brainstorms
 * - Manual entries
 */
export default function EssayIdeasLibrary({ onDevelopIdea, onClose, selectedIdeaId, refreshKey, onNavigateToOutlines, hideHeader = false }) {
  const { currentUser } = useFamilyContext();
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, hidden_competencies, character_identity, strategic_assessment, manual
  const [searchQuery, setSearchQuery] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [developingEssay, setDevelopingEssay] = useState(null);
  const selectedIdeaRef = React.useRef(null);

  // Fetch essay ideas from database
  const fetchEssayIdeas = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('college_journey_essay_ideas')
        .select('*')
        .eq('user_id', currentUser.id)
        .is('essay_slot_id', null) // Exclude ideas linked to essay slots (those belong in the slots view)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      console.log(`✅ Loaded ${data.length} essay ideas`);

      // Debug: Check if development_responses are included
      const withResponses = data.filter(idea => idea.development_responses && Object.keys(idea.development_responses).length > 0);
      if (withResponses.length > 0) {
        console.log(`📋 ${withResponses.length} ideas have saved responses`);
        console.log('First idea with responses:', withResponses[0].title, withResponses[0].development_responses);
      }

      setEssays(data || []);
    } catch (err) {
      console.error('Error fetching essay ideas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);
  
  // Fetch essay ideas on mount and when refreshKey changes
  useEffect(() => {
    console.log('📚 EssayIdeasLibrary: Fetching ideas (refreshKey:', refreshKey, ')');
    fetchEssayIdeas();
  }, [fetchEssayIdeas, refreshKey]);

  // Scroll to selected idea when it's set and essays are loaded
  useEffect(() => {
    if (selectedIdeaId && essays.length > 0 && selectedIdeaRef.current) {
      // Small delay to ensure rendering is complete
      setTimeout(() => {
        selectedIdeaRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 300);
    }
  }, [selectedIdeaId, essays]);

  const toggleFavorite = async (ideaId, currentlyFavorited) => {
    try {
      const { error: updateError } = await supabase
        .from('college_journey_essay_ideas')
        .update({ is_favorite: !currentlyFavorited })
        .eq('id', ideaId);

      if (updateError) throw updateError;

      // Update local state
      setEssays(essays.map(idea =>
        idea.id === ideaId ? { ...idea, is_favorite: !currentlyFavorited } : idea
      ));
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const deleteIdea = async (ideaId) => {
    if (!confirm('Delete this essay idea? This cannot be undone.')) return;

    try {
      const { error: deleteError } = await supabase
        .from('college_journey_essay_ideas')
        .delete()
        .eq('id', ideaId);

      if (deleteError) throw deleteError;

      setEssays(essays.filter(idea => idea.id !== ideaId));
    } catch (err) {
      console.error('Error deleting essay idea:', err);
      alert('Failed to delete essay idea. Please try again.');
    }
  };

  // Filter essay ideas
  const filteredEssays = essays.filter(idea => {
    // Filter by source type
    if (filter !== 'all' && idea.source_type !== filter) return false;

    // Filter by favorites
    if (favoritesOnly && !idea.is_favorite) return false;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        idea.title?.toLowerCase().includes(query) ||
        idea.description?.toLowerCase().includes(query) ||
        idea.content?.toLowerCase().includes(query) ||
        idea.essay_angle?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Group by source type
  const groupedEssays = filteredEssays.reduce((acc, idea) => {
    const sourceType = idea.source_type || 'manual';

    if (!acc[sourceType]) {
      acc[sourceType] = [];
    }
    acc[sourceType].push(idea);
    return acc;
  }, {});

  // Source type information
  const sourceInfo = {
    hidden_competencies: {
      label: 'Hidden Competencies',
      description: 'Ideas from your strengths and skills interview',
      icon: Sparkles
    },
    character_identity: {
      label: 'Character & Identity',
      description: 'Ideas from your values and identity interview',
      icon: User
    },
    strategic_assessment: {
      label: 'Strategic Assessment',
      description: 'Strategically positioned ideas based on your profile',
      icon: Brain
    },
    quick_brainstorm: {
      label: 'Quick Brainstorm',
      description: 'Ideas from quick brainstorming sessions',
      icon: Zap
    },
    manual: {
      label: 'Manual Entry',
      description: 'Ideas you added manually',
      icon: Plus
    }
  };

  // Slot type labels and colors
  const slotTypeInfo = {
    supplemental: {
      label: 'Supplemental Essay',
      dotColor: 'bg-teal-500'
    },
    personal_statement: {
      label: 'Personal Statement',
      dotColor: 'bg-indigo-500'
    },
    short_answer: {
      label: 'Short Answer',
      dotColor: 'bg-gold-400'
    },
    why_us: {
      label: 'Why Us',
      dotColor: 'bg-azure-500'
    },
    activity: {
      label: 'Activity Essay',
      dotColor: 'bg-magenta-500'
    }
  };

  // College category colors
  const getCategoryColor = (category) => {
    switch(category) {
      case 'reach': return 'bg-coral-500';
      case 'target': return 'bg-teal-500';
      case 'safety': return 'bg-emerald-500';
      default: return 'bg-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 bg-azure-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lightbulb className="w-6 h-6 text-azure-600 animate-pulse" />
          </div>
          <p className="text-slate-600">Loading your essay ideas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 mb-4">Failed to load essay ideas: {error}</p>
        <button
          onClick={fetchEssayIdeas}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={hideHeader ? '' : 'min-h-screen bg-stone-50 font-[\'Reddit_Sans\',system-ui,sans-serif]'}>
      {/* Header */}
      {!hideHeader && (
        <div className="bg-slate-800 rounded-lg p-4 border-b-2 border-slate-900">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-stone-50">Topics & Ideas</h1>
            </div>
            <p className="text-sm text-stone-300">
              {filteredEssays.length} {filteredEssays.length === 1 ? 'essay prompt' : 'essay prompts'} across {Object.keys(groupedEssays).length} {Object.keys(groupedEssays).length === 1 ? 'college' : 'colleges'}
              {favoritesOnly && ' (favorites only)'}
            </p>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="bg-white border-b-2 border-slate-900">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input
                type="text"
                placeholder="Search essay prompts and colleges..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-stone-500 focus:border-azure-500 focus:outline-none transition-colors"
              />
            </div>
            
            {/* Essay Type Filter */}
            <div className="relative">
              <select 
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="appearance-none px-4 py-3 pr-10 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 font-medium focus:border-azure-500 focus:outline-none transition-colors cursor-pointer"
              >
                <option value="all">All Types</option>
                <option value="supplemental">Supplemental Essays</option>
                <option value="personal_statement">Personal Statements</option>
                <option value="short_answer">Short Answers</option>
                <option value="why_us">Why Us</option>
                <option value="activity">Activity Essays</option>
              </select>
            </div>

            {/* Favorites Button */}
            <button
              onClick={() => setFavoritesOnly(!favoritesOnly)}
              className={`px-4 py-3 bg-white border rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                favoritesOnly
                  ? 'border-coral-500 text-coral-600 bg-coral-50'
                  : 'border-slate-300 text-slate-900 hover:border-slate-900 hover:shadow-sm'
              }`}
            >
              <Heart className={`w-4 h-4 ${favoritesOnly ? 'fill-coral-600' : ''}`} />
              Favorites
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={hideHeader ? '' : 'max-w-7xl mx-auto px-8 py-12'}>
        {/* Empty State */}
        {filteredEssays.length === 0 && (
          <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {searchQuery || favoritesOnly ? 'No matching essay prompts' : 'No essay prompts yet'}
            </h3>
            <p className="text-slate-600 mb-4">
              {searchQuery || favoritesOnly
                ? 'Try adjusting your filters or search query'
                : 'Add colleges to see their essay requirements'}
            </p>
          </div>
        )}

        {/* Essay Ideas Grouped by Source */}
        <div className="space-y-12">
          {Object.entries(groupedEssays).map(([sourceType, sourceEssays]) => {
            const info = sourceInfo[sourceType] || sourceInfo.manual;
            
            // Get color classes for this source type
            const getColorClasses = (type) => {
              switch(type) {
                case 'hidden_competencies':
                  return {
                    headerBg: 'bg-azure-50',
                    headerBorder: 'border-azure-800',
                    headerDot: 'bg-azure-400',
                    headerText: 'text-azure-900',
                    descBorder: 'border-azure-800',
                    storyDot: 'bg-azure-500',
                    buttonHover: 'hover:bg-azure-600',
                    tagBorder: 'border-azure-300',
                    tagText: 'text-azure-500'
                  };
                case 'character_identity':
                  return {
                    headerBg: 'bg-violet-50',
                    headerBorder: 'border-violet-800',
                    headerDot: 'bg-violet-400',
                    headerText: 'text-violet-900',
                    descBorder: 'border-violet-800',
                    storyDot: 'bg-violet-500',
                    buttonHover: 'hover:bg-violet-600',
                    tagBorder: 'border-violet-300',
                    tagText: 'text-violet-500'
                  };
                case 'strategic_assessment':
                  return {
                    headerBg: 'bg-slate-900',
                    headerBorder: 'border-indigo-800',
                    headerDot: 'bg-indigo-400',
                    headerText: 'text-stone-50',
                    descBorder: 'border-indigo-800',
                    storyDot: 'bg-indigo-500',
                    buttonHover: 'hover:bg-indigo-600',
                    tagBorder: 'border-indigo-300',
                    tagText: 'text-indigo-500'
                  };
                case 'quick_brainstorm':
                  return {
                    headerBg: 'bg-gold-50',
                    headerBorder: 'border-gold-800',
                    headerDot: 'bg-gold-400',
                    headerText: 'text-gold-900',
                    descBorder: 'border-gold-800',
                    storyDot: 'bg-gold-500',
                    buttonHover: 'hover:bg-gold-600',
                    tagBorder: 'border-gold-300',
                    tagText: 'text-gold-500'
                  };
                default:
                  return {
                    headerBg: 'bg-stone-50',
                    headerBorder: 'border-slate-800',
                    headerDot: 'bg-slate-400',
                    headerText: 'text-slate-900',
                    descBorder: 'border-slate-800',
                    storyDot: 'bg-slate-500',
                    buttonHover: 'hover:bg-slate-600',
                    tagBorder: 'border-slate-300',
                    tagText: 'text-slate-500'
                  };
              }
            };
            
            const colors = getColorClasses(sourceType);

            return (
              <div key={sourceType}>
                {/* Source Section Header */}
                <div className={`my-8 border ${colors.headerBorder} rounded-xl overflow-hidden inline-block`}>
                  <div className={`px-6 py-1 ${colors.headerBg}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${colors.headerDot}`}></div>
                      <h2 className={`${colors.headerText} font-semibold text-sm uppercase tracking-wide`}>{info.label}</h2>
                      <span className="text-stone-400 text-sm">({sourceEssays.length})</span>
                    </div>
                  </div>
                </div>

                {/* Ideas Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {sourceEssays.map((essay) => {
                    const isSelected = essay.id === selectedIdeaId;
                    
                    return (
                      <div 
                        key={essay.id} 
                        ref={isSelected ? selectedIdeaRef : null}
                        className={`bg-white border-2 rounded-xl overflow-hidden hover:shadow-xl transition-all ${
                          isSelected ? 'border-teal-600 shadow-xl' : 'border-slate-900'
                        }`}
                      >
                        {/* Card Header */}
                        <div className="bg-white">
                          <div className={`px-8 pt-8 pb-4 ${colors.headerBg}`}>
                            {/* Question Status Badge */}
                            {(() => {
                              const hasQuestions = !!essay.development_questions;
                              const hasResponses = essay.development_responses && Object.keys(essay.development_responses).length > 0;

                              if (!hasQuestions && !hasResponses) return null;

                              // Count total questions
                              const totalQuestions = hasQuestions ? essay.development_questions.length : 0;

                              // Count answered questions
                              let answeredCount = 0;
                              if (hasResponses && typeof essay.development_responses === 'object') {
                                // Check if it's the new flat format (has string values directly)
                                const hasDirectValues = Object.values(essay.development_responses).some(
                                  val => typeof val === 'string'
                                );

                                if (hasDirectValues) {
                                  // New flat format: { question_id: "answer" }
                                  answeredCount = Object.keys(essay.development_responses).filter(
                                    key => {
                                      const val = essay.development_responses[key];
                                      return typeof val === 'string' && val.trim();
                                    }
                                  ).length;
                                } else {
                                  // Old stage-based format: { stage1: { timestamp, responses: {...} } }
                                  Object.values(essay.development_responses).forEach(stage => {
                                    if (stage.responses) {
                                      answeredCount += Object.keys(stage.responses).filter(
                                        key => {
                                          const val = stage.responses[key];
                                          return typeof val === 'string' && val.trim();
                                        }
                                      ).length;
                                    }
                                  });
                                }
                              }

                              const isComplete = totalQuestions > 0 && answeredCount >= totalQuestions;
                              const isInProgress = answeredCount > 0 && !isComplete;

                              return (
                                <div className="mb-3">
                                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                                    isComplete
                                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                      : isInProgress
                                      ? 'bg-amber-100 text-amber-700 border border-amber-300'
                                      : 'bg-blue-100 text-blue-700 border border-blue-300'
                                  }`}>
                                    {isComplete && (
                                      <>
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        <span>Complete ({answeredCount}/{totalQuestions})</span>
                                      </>
                                    )}
                                    {isInProgress && (
                                      <>
                                        <Loader2 className="w-3.5 h-3.5" />
                                        <span>In Progress ({answeredCount}/{totalQuestions})</span>
                                      </>
                                    )}
                                    {!isInProgress && !isComplete && hasQuestions && (
                                      <>
                                        <MessageSquare className="w-3.5 h-3.5" />
                                        <span>Ready ({totalQuestions} questions)</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                            <div className="flex items-start justify-between gap-4 mb-3 pt-1">
                              <h3 className="text-md font-semibold text-stone-50 leading-tight flex-1">
                                {essay.title}
                              </h3>
                              <button
                                onClick={() => toggleFavorite(essay.id, essay.is_favorite)}
                                className="text-stone-400 hover:text-coral-500 transition-colors flex-shrink-0"
                              >
                                <Heart className={`w-5 h-5 ${essay.is_favorite ? 'fill-coral-500 text-coral-500' : ''}`} />
                              </button>
                            </div>
                          </div>
                          
                          <div className="px-8 py-10 bg-white">
                            {/* Description - Emphasized Box */}
                            <div className={`border ${colors.descBorder} rounded-lg p-5 mb-6`}>
                              <p className="text-md text-slate-900 leading-relaxed font-regular">{essay.description}</p>
                            </div>

                            {/* Essay Angle */}
                            {essay.essay_angle && (
                              <p className="text-sm text-slate-700 leading-relaxed mb-4">
                                <span className="font-semibold">Angle:</span> {essay.essay_angle}
                              </p>
                            )}
                          </div>

                          {/* Why This Works - For Strategic Assessment ideas without narrative arc */}
                          {!essay.narrative_arc && essay.why_it_works && essay.source_type === 'strategic_assessment' && (
                            <div className="px-8 py-6 border-t border-stone-200 bg-stone-50">
                              <div className="flex items-start gap-3 mb-3">
                                <Sparkles className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
                                <h4 className="text-xs font-semibold text-slate-900 uppercase tracking-wide">Why This Works</h4>
                              </div>
                              <p className="text-sm text-slate-800 leading-relaxed">
                                {essay.why_it_works}
                              </p>
                            </div>
                          )}
                          
                          {/* Differentiator - For Strategic Assessment ideas */}
                          {essay.differentiator && essay.source_type === 'strategic_assessment' && (
                            <div className="px-8 py-6 border-t border-stone-200 bg-white">
                              <div className="flex items-start gap-3 mb-3">
                                <Star className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" />
                                <h4 className="text-xs font-semibold text-slate-900 uppercase tracking-wide">What Makes This Unique</h4>
                              </div>
                              <p className="text-sm text-slate-800 leading-relaxed">
                                {essay.differentiator}
                              </p>
                            </div>
                          )}

                          {/* Story Structure */}
                          {essay.narrative_arc && (
                            <div className="pt-2 text-stone-50">
                              <div className="flex items-center gap-2 px-8 py-2 bg-slate-800">
                                <div className={`w-2 h-2 rounded-full ${colors.storyDot}`}></div>
                                <h4 className="text-xs font-semibold text-stone-50 uppercase tracking-wide">Story Structure</h4>
                              </div>
                              <div className="space-y-3 px-8 py-8 bg-stone-100">
                                {(() => {
                                  // Handle narrative_arc as either array or string
                                  let steps;
                                  if (Array.isArray(essay.narrative_arc)) {
                                    steps = essay.narrative_arc;
                                  } else {
                                    // Split by arrows (→ or ->) first, then by newlines
                                    steps = essay.narrative_arc
                                      .split(/→|->/)
                                      .flatMap(s => s.split('\n'))
                                      .map(s => s.trim())
                                      .filter(s => s.length > 0);
                                  }
                                  
                                  return steps.map((step, idx) => {
                                    // Strip leading bullets or numbers from the step text
                                    const cleanStep = step.replace(/^[•*\d+.)\s]+/, '').trim();
                                    
                                    return (
                                      <div key={idx} className="flex items-start gap-4">
                                        <div className={`w-6 h-6 ${colors.storyDot} text-white rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-semibold`}>
                                          {idx + 1}
                                        </div>
                                        <p className="text-sm text-slate-900 leading-relaxed flex-1 pt-0.5">{cleanStep}</p>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          )}

                          {/* Interview Quote */}
                          {essay.source_quote && (
                            <div className="px-8 py-6 border-t border-slate-800">
                              <div className="flex items-start gap-3 mb-3">
                                <Sparkles className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" />
                                <h4 className="text-xs font-semibold text-slate-900 uppercase tracking-wide">From Your Interview</h4>
                              </div>
                              <p className="text-sm text-slate-800 italic leading-relaxed pl-8">"{essay.source_quote}"</p>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="px-6 py-2 flex items-center gap-3">
                            <button
                              onClick={() => setDevelopingEssay(essay)}
                              className={`flex-1 px-6 py-4 bg-slate-900 text-white border border-slate-900 rounded-lg font-semibold text-sm ${colors.buttonHover} transition-all active:scale-95 flex items-center justify-center gap-2 shadow-sm`}
                            >
                              <ExternalLink className="w-4 h-4" />
                              {essay.development_questions ? 'Continue Development' : 'Develop Essay'}
                            </button>

                            {/* Duplicate & Regenerate button - only show if questions exist */}
                            {essay.development_questions && (
                              <button
                                onClick={async () => {
                                  if (!confirm(`Create a new version of "${essay.title}" with fresh questions?\n\nThe original will be kept with "V1" appended to its title.`)) {
                                    return;
                                  }

                                  try {
                                    // Update original to V1
                                    await supabase
                                      .from('college_journey_essay_ideas')
                                      .update({ title: `${essay.title} V1` })
                                      .eq('id', essay.id);

                                    // Create duplicate with V2 suffix and no questions
                                    const { data: newIdea, error } = await supabase
                                      .from('college_journey_essay_ideas')
                                      .insert({
                                        user_id: currentUser.id,
                                        title: `${essay.title} V2`,
                                        description: essay.description,
                                        content: essay.content,
                                        essay_angle: essay.essay_angle,
                                        essay_type: essay.essay_type,
                                        source_type: essay.source_type,
                                        prompt: essay.prompt,
                                        word_limit: essay.word_limit,
                                        school_name: essay.school_name,
                                        college_id: essay.college_id,
                                        essay_slot_id: essay.essay_slot_id,
                                        supplemental_context: essay.supplemental_context,
                                        interview_data: essay.interview_data,
                                        // Explicitly omit development_questions to force regeneration
                                        development_questions: null,
                                        development_responses: null,
                                        development_count: 0
                                      })
                                      .select()
                                      .single();

                                    if (error) throw error;

                                    // Refresh list and open new version for development
                                    await fetchEssayIdeas();
                                    setDevelopingEssay(newIdea);
                                  } catch (err) {
                                    console.error('Failed to duplicate idea:', err);
                                    alert('Failed to create new version. Please try again.');
                                  }
                                }}
                                className="p-4 bg-blue-100 rounded-lg text-blue-700 hover:bg-blue-200 transition-all flex items-center gap-2 text-sm font-semibold"
                                title="Duplicate & regenerate questions"
                              >
                                <Shuffle className="w-4 h-4" />
                              </button>
                            )}

                            <button
                              onClick={() => deleteIdea(essay.id)}
                              className="p-4 bg-stone-200 rounded-lg text-slate-700 hover:border-coral-500 hover:text-coral-500 hover:bg-coral-50 transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Tags */}
                          {essay.potential_prompts && essay.potential_prompts.length > 0 && (
                            <div className="px-8 py-6 bg-white">
                              <div className="space-y-2">
                                <div>
                                  <p className="text-[10px] font-bold text-stone-600 uppercase tracking-wide mb-2">Potential Prompts</p>
                                  <div className="flex flex-wrap gap-2">
                                    {essay.potential_prompts.map((prompt, idx) => (
                                      <span key={idx} className={`px-3 py-2 bg-white border ${colors.tagBorder} rounded-lg text-xs font-regular ${colors.tagText}`}>
                                        {prompt}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Essay Topic Development Modal */}
      {developingEssay && (
        <EssayTopicDevelopment
          essayIdea={developingEssay}
          onComplete={(result) => {
            console.log('Development complete:', result);
            setDevelopingEssay(null);
            // Refresh essays to get updated development_count
            fetchEssayIdeas();
          }}
          onClose={() => setDevelopingEssay(null)}
          onNavigateToOutlines={onNavigateToOutlines}
        />
      )}
    </div>
  );
}
